import { NewDealSolarProduct } from "../model/solar-product/new-deal-solar-product";
import { SolarProductWithRecommendations } from "../model/solar-product/solar-product-with-recommendations";
import { SolarProduct } from "../model/solar-product/solar-product";
import { StoreProductDetail as LeadsProduct} from "../model/leads/leads";
import { PaginatedList } from "../model/common/paginated-list";
import { NewSolarProduct } from "../model/solar-product/new-solar-product";
import { ExistingSolarProduct } from "../model/solar-product/existing-solar-product";
import { CreateOrEditLoanProduct } from "../model/solar-product/loan-product";
import { CategoryWithProduct } from "../model/solar-product/category-with-product";
import { apiSlice } from "../stores/base/api-slice";
import { Money } from "../model/common/money";

const serviceBaseurl = "/product"

export const solarProductEndpoints = apiSlice.injectEndpoints({
  endpoints: builder => ({
    viewProduct: builder.query<SolarProductWithRecommendations, string>({
      query: id => ({
        url: `${serviceBaseurl}/solar-product/get-product-details`,
        params: { id },
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "SolarProducts", id }, "SolarProducts"],
      transformResponse: (response: SolarProductWithRecommendations) => ({
        ...response,
          solarProduct: {
            ...response.solarProduct,
            solarProduct: {
              ...response.solarProduct.solarProduct,
              price: new Money(response.solarProduct.solarProduct.price),
              installationCostForBungalow: new Money(response.solarProduct.solarProduct.installationCostForBungalow),
              installationCostForFloorForStoryBuilding: new Money(response.solarProduct.solarProduct.installationCostForFloorForStoryBuilding),
            },
            availableOffers: response.solarProduct.availableOffers.map(offer => ({
              ...offer,
              price: new Money(offer.price),
            })),
          }
      }),
    }),
    
    newDeals: builder.query<NewDealSolarProduct[], void>({
      query: () => ({
        url: `${serviceBaseurl}/solar-product/new-deals`,
        method: "GET",
      }),
      providesTags: ["SolarProducts"],
      transformResponse: (response: NewDealSolarProduct[]) =>
        response.map(item => ({
          ...item,
          price: new Money(item.price),
          images: item.images.map(image => ({
            ...image,
            imageUrl: image.imageUrl,
          })),
        })),
    }),
    
    getById: builder.query<SolarProduct, string>({
      query: id => ({
        url: `${serviceBaseurl}/solar-product/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "SolarProducts", id }],
      transformResponse: (response: SolarProduct) => ({
        ...response,
          price: new Money(response.price),
          installationCostForBungalow: new Money(response.installationCostForBungalow),
          installationCostForFloorForStoryBuilding: new Money(response.installationCostForFloorForStoryBuilding),
      }),
    }),
    
    getPaged: builder.query<PaginatedList<SolarProduct>, { page: number, pageSize: number }>({
      query: ({ page, pageSize }) => ({
        url: `/solar-product`,
        params: { page, pageSize },
        method: "GET",
      }),
      providesTags: ["SolarProducts"],
      transformResponse: (response: PaginatedList<SolarProduct>) => ({
        ...response,
        items: response.items.map(item => ({
          ...item,
          price: new Money(item.price),
          installationCostForBungalow: new Money(item.installationCostForBungalow),
          installationCostForFloorForStoryBuilding: new Money(item.installationCostForFloorForStoryBuilding),
        })),
      }),
    }),
    getProductsWithLoan: builder.query<PaginatedList<LeadsProduct>, { page: number, pageSize: number }>({
      query: ({ page, pageSize }) => ({
        url: `${serviceBaseurl}/solar-product/products-with-loan/page`,
        params: { page, pageSize },
        method: "GET",
      }),
      providesTags: ["SolarProducts"],
    }),
    
    addProduct: builder.mutation<void, FormData>({
      query: (formData) => ({
        url: `${serviceBaseurl}/solar-product/save`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["SolarProducts"],
    }),
    
    editProduct: builder.mutation<void, { id: string, formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `${serviceBaseurl}/solar-product/${id}/edit`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "SolarProducts", id }, "SolarProducts"],
    }),
    
    archiveProduct: builder.mutation<void, string>({
      query: (id) => ({
        url: `${serviceBaseurl}/solar-product/${id}/flip-arhive-status-for-solar-product`,
        method: "POST",
      }),
      invalidatesTags: ["SolarProducts"],
    }),
    
    pauseProduct: builder.mutation<void, string>({
      query: (id) => ({
        url: `${serviceBaseurl}/solar-product/pause/${id}`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [{ type: "SolarProducts", id }, "SolarProducts"],
    }),
    
    getProductsByCategory: builder.query<any, { category: string, queryParams: string }>({
      query: ({ category, queryParams }) => ({
        url: `/solar-product/category/${category}?${queryParams}`,
        method: "GET",
      }),
      providesTags: ["SolarProducts"],
    }),
    
    getNewPaged: builder.query<PaginatedList<NewSolarProduct>, { page: number, pageSize: number }>({
      query: ({ page, pageSize }) => ({
        url: `${serviceBaseurl}/solar-product/new`,
        params: { page, pageSize },
        method: "GET",
      }),
      providesTags: ["SolarProducts"],
      transformResponse: (response: PaginatedList<NewSolarProduct>) => ({
        ...response,
        items: response.items.map(item => ({
          ...item,
          dateAdded: new Date(item.dateAdded),
          cost: new Money(item.cost)
        })),
      }),
    }),
    
    getExistingPaged: builder.query<PaginatedList<ExistingSolarProduct>, { page: number, pageSize: number }>({
      query: ({ page, pageSize }) => ({
        url: `/solar-product/existing`,
        params: { page, pageSize },
        method: "GET",
      }),
      providesTags: ["SolarProducts"],
      transformResponse: (response: PaginatedList<ExistingSolarProduct>) => ({
        ...response,
        items: response.items.map(item => ({
          ...item,
          cost: new Money(item.cost),
        })),
      }),
    }),
    
    addLoanProduct: builder.mutation<void, { productId: string, loanProduct: CreateOrEditLoanProduct }>({
      query: ({ productId, loanProduct }) => ({
        url: `${serviceBaseurl}/solar-product/${productId}/loan-product`,
        method: "POST",
        body: { ...loanProduct},
      }),
      invalidatesTags: (result, error, { productId }) => [{ type: "SolarProducts", id: productId }, "SolarProducts"],
    }),
    
    editLoanProduct: builder.mutation<{ otpSent: boolean }, { productId: string, loanProduct: CreateOrEditLoanProduct, actionCode?: string }>({
      query: ({ productId, loanProduct, actionCode }) => ({
        url: `${serviceBaseurl}/solar-product/${productId}/loan-product`,
        method: "PUT",
        body: { ...loanProduct, actionCode },
      }),
      invalidatesTags: (result, error, { productId }) => [{ type: "SolarProducts", id: productId }, "SolarProducts"],
    }),
    
    adminPauseProduct: builder.mutation<void, { id: string, actionCode?: string }>({
      query: ({ id, actionCode }) => ({
        url: `${serviceBaseurl}/solar-product/admin/pause/${id}`,
        method: "POST",
        body: { actionCode },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "SolarProducts", id }, "SolarProducts"],
    }),
    
    recommendBestFit: builder.query<SolarProduct[], { powerNeeded: number, monthsToPay: number, monthlyBudget: number }>({
      query: ({ powerNeeded, monthsToPay, monthlyBudget }) => ({
        url: `${serviceBaseurl}/solar-product/recommend/best-fit`,
        params: { powerNeeded, monthsToPay, monthlyBudget },
        method: "GET",
      }),
      transformResponse: (response: { solarProductsThatFitTheBest: SolarProduct[] }) => response.solarProductsThatFitTheBest,
      providesTags: ["SolarProducts"],
    }),
    
    recommendBudgetFit: builder.query<
      PaginatedList<SolarProduct>, 
      { page: number, pageSize: number, monthsToPay: number, monthlyBudget: number }
    >({
      query: ({ page, pageSize, monthsToPay, monthlyBudget }) => ({
        url: `${serviceBaseurl}/solar-product/recommend/budget`,
        params: { page, pageSize, monthsToPay, monthlyBudget },
        method: "GET",
      }),
      transformResponse: (response: { solarProductsThatFitTheBudget: PaginatedList<SolarProduct> }) => 
        response.solarProductsThatFitTheBudget,
      providesTags: ["SolarProducts"],
    }),
    
    recommendPowerFit: builder.query<
      PaginatedList<SolarProduct>, 
      { page: number, pageSize: number, powerNeeded: number }
    >({
      query: ({ page, pageSize, powerNeeded }) => ({
        url: `${serviceBaseurl}/solar-product/recommend/power-needs`,
        params: { page, pageSize, powerNeeded },
        method: "GET",
      }),
      transformResponse: (response: { solarProductsThatFitThePowerNeeds: PaginatedList<SolarProduct> }) => 
        response.solarProductsThatFitThePowerNeeds,
      providesTags: ["SolarProducts"],
    }),
    
    getProductsByCategories: builder.query<Array<CategoryWithProduct>, void>({
      query: () => ({
        url: `/solar-product/get-product-details-by-categories`,
        method: "GET",
      }),
      providesTags: ["SolarProducts"],
    }),
    
    searchByName: builder.query<Array<SolarProduct>, string>({
      query: (search) => ({
        url: `/solar-product/search/${search}`,
        method: "GET",
      }),
      providesTags: ["SolarProducts"],
    }),
  }),
});

export const {
  useLazyViewProductQuery,
  useLazyNewDealsQuery,
  useGetByIdQuery,
  useLazyGetByIdQuery,
  useGetPagedQuery,
  useLazyGetPagedQuery,
  useAddProductMutation,
  useEditProductMutation,
  useArchiveProductMutation,
  usePauseProductMutation,
  useLazyGetProductsByCategoryQuery,
  useGetNewPagedQuery, 
  useLazyGetNewPagedQuery,
  useGetExistingPagedQuery,
  useLazyGetExistingPagedQuery,
  useAddLoanProductMutation,
  useEditLoanProductMutation,
  useAdminPauseProductMutation,
  useLazyRecommendBestFitQuery,
  useLazyRecommendBudgetFitQuery,
  useLazyRecommendPowerFitQuery,
  useGetProductsByCategoriesQuery,
  useLazyGetProductsByCategoriesQuery,
  useLazySearchByNameQuery,
  useLazyGetProductsWithLoanQuery,
} = solarProductEndpoints;